import React from 'react';
import { useTranslation } from 'react-i18next';
import type { LucideIcon } from 'lucide-react';
import { AlertTriangle, ArrowLeft, RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { FeedbackState } from '@/components/ui/FeedbackState';

interface ErrorStateProps {
  /** Error object or message */
  error?: Error | string | null;
  /** Custom error title */
  title?: string;
  /** Custom error message */
  message?: string;
  /** Icon to display */
  icon?: LucideIcon;
  /** Retry handler */
  onRetry?: () => void;
  /** Back navigation handler */
  onBack?: () => void;
  /** Additional actions */
  actions?: React.ReactNode;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Additional CSS classes */
  className?: string;
}

export function ErrorState({
  error,
  title,
  message,
  icon: Icon = AlertTriangle,
  onRetry,
  onBack,
  actions,
  size = 'md',
  className = '',
}: ErrorStateProps) {
  const { t } = useTranslation();

  const getErrorMessage = () => {
    if (message) return message;
    if (typeof error === 'string') return error;
    if (error?.message) return error.message;
    return t('general.unknownError');
  };

  const getErrorTitle = () => {
    if (title) return title;
    return t('general.errorOccurred');
  };

  const action =
    onRetry || onBack || actions ? (
      <div className='flex flex-col sm:flex-row items-center justify-center gap-3'>
        {onRetry && (
          <Button onClick={onRetry} variant='primary'>
            <RefreshCw className='h-4 w-4 mr-2' />
            {t('actions.retry')}
          </Button>
        )}

        {onBack && (
          <Button onClick={onBack} variant='outline'>
            <ArrowLeft className='h-4 w-4 mr-2' />
            {t('actions.back')}
          </Button>
        )}

        {actions}
      </div>
    ) : undefined;

  return (
    <FeedbackState
      icon={Icon}
      title={getErrorTitle()}
      description={getErrorMessage()}
      action={action}
      tone='danger'
      size={size}
      headingLevel='h2'
      className={className}
    />
  );
}

interface PageErrorStateProps {
  error?: Error | string | null;
  title?: string;
  message?: string;
  icon?: LucideIcon;
  onRetry?: () => void;
  onBack?: () => void;
  actions?: React.ReactNode;
}

export function PageErrorState(props: PageErrorStateProps) {
  return (
    <div className='min-h-screen bg-light-50 dark:bg-gray-900 flex items-center justify-center'>
      <div className='max-w-md w-full mx-4'>
        <ErrorState {...props} size='lg' />
      </div>
    </div>
  );
}
